// Quiz-style presets — let players/teachers pick HOW words are tested,
// overriding each level's built-in challenge mix. Used by both solo
// (PackLevelPicker → LessonPlay) and multiplayer (RaceLobby → RacePlay).

import type { ChallengeKind } from "@shared/types";

export type QuizStyleId = "default" | "all" | "to-image" | "to-audio" | "spell";

export interface QuizStyle {
  id: QuizStyleId;
  label: string;
  hint: string;
  icon: string;
  /** Allowed challenge kinds. null = keep the level's own kinds. */
  kinds: ChallengeKind[] | null;
}

export const QUIZ_STYLES: QuizStyle[] = [
  {
    id: "default",
    label: "Level default",
    hint: "Each level's own style",
    icon: "🎯",
    kinds: null,
  },
  {
    id: "all",
    label: "Mix it up",
    hint: "All question styles",
    icon: "🎲",
    kinds: ["audio-to-image", "image-to-audio", "image-to-word", "audio-to-english", "word-to-image", "spell"],
  },
  {
    id: "to-image",
    label: "Listen → Picture",
    hint: "Hear it, pick the picture or word",
    icon: "🔊",
    kinds: ["audio-to-image", "audio-to-english"],
  },
  {
    id: "to-audio",
    label: "Picture → Listen",
    hint: "See it, pick the spoken word",
    icon: "🖼️",
    kinds: ["image-to-audio"],
  },
  {
    id: "spell",
    label: "Spelling",
    hint: "Tap letters to spell the word",
    icon: "🔤",
    kinds: ["spell"],
  },
];

export function getQuizStyle(id: string | null | undefined): QuizStyle {
  return QUIZ_STYLES.find((q) => q.id === id) ?? QUIZ_STYLES[0]!;
}

/**
 * Effective challenge kinds for a run: the chosen preset, or — when the player
 * left it on "Level default" — the level's own kinds.
 */
export function resolveQuizKinds(
  id: string | null | undefined,
  levelDefault: ChallengeKind[] | null | undefined,
): ChallengeKind[] | null {
  const style = getQuizStyle(id);
  return style.kinds ?? levelDefault ?? null;
}
